import React, { useContext, useEffect, useState } from 'react';
import { Icon, Link, TextLarge } from '@hz-design-system/web-ui';
import { getFormattedNumber } from '@ecg-marktplaats/aurora-js-formatters';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import { trackGAEvent } from '../../../../utils/gaTracking';
import { GA_PARAMETER, GA_TRACK_EVENTS_ACTIONS } from '../../../../utils/constants';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import createCustomDimension from '../../../../utils/createCustomDimension';
import TextSkeleton from '../../../../components/Skeleton/TextSkeleton';

import { TListingSellerInfo } from 'types/TListing';
import { TSellerProfileClient } from 'types/TSellerProfile';

import Classes from './SellerInfoSmall.scss';

type TSellerReviewsProps = {
  seller: TListingSellerInfo;
  profile: TSellerProfileClient;
};

let SellerReviewDialog;

const SellerReviews = ({ seller, profile }: TSellerReviewsProps) => {
  const { t } = useI18nContext();
  const { links: { seller: sellerLinks = {} } = {}, hasLicensePlate } = useContext(EnvironmentContext);
  const { name, showProfilePicture } = seller;
  const [isSellerReviewDialogLoaded, setSellerReviewDialogLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { numberOfReviews, externalSource, externalReviews, averageScore, profilePictures, provider } = profile;
  const isProfileLoaded = Object.keys(profile).length !== 0;
  const minScore = 0;
  const maxScore = 5;
  const isRatingValid = !!averageScore && averageScore >= minScore && averageScore <= maxScore;

  useEffect(() => {
    if (numberOfReviews || averageScore) {
      const gaEvent = profile.externalSource
        ? GA_TRACK_EVENTS_ACTIONS.SellerExternalAvailable
        : GA_TRACK_EVENTS_ACTIONS.SellerRatingsAvailable;
      trackGAEvent(gaEvent, '', {
        ...createCustomDimension({
          dimension: GA_PARAMETER.AD_HAS_LICENSE_PLATE,
          payload: hasLicensePlate,
        }),
      });
    }
  }, [numberOfReviews, averageScore, hasLicensePlate, profile.externalSource]);

  const loadSellerReviewDialog = async () => {
    if (!isSellerReviewDialogLoaded) {
      try {
        if (externalSource) {
          SellerReviewDialog = (
            await import(/* webpackChunkName: "SellerExternalReviewDialog" */ './SellerExternalReviewDialog')
          ).default;
        } else {
          SellerReviewDialog = (
            await import(/* webpackChunkName: "SellerInternalReviewDialog" */ './SellerReviewDialog')
          ).default;
        }
        setSellerReviewDialogLoaded(true);
      } catch (err) {}
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const gaEvent = profile.externalSource
      ? GA_TRACK_EVENTS_ACTIONS.SellerExternalRatingsClicked
      : GA_TRACK_EVENTS_ACTIONS.SellerRatingsClicked;
    trackGAEvent(gaEvent, `${profile.averageScore}`);
    loadSellerReviewDialog();
    setIsOpen(true);
  };
  const handleClose = () => {
    setIsOpen(false);
  };

  const props = profile.externalSource
    ? {
        title: t('sellerInfo.externalReviewDialogTitle'),
        reviewsText: t('sellerInfo.externalReviewDialogText', {
          count: numberOfReviews,
        }),
        providedBy: t('sellerInfo.externalReviewDialogProvided', { provider }),
        onClose: handleClose,
        sellerName: name,
        averageScore,
        reviews: externalReviews,
        showProfilePicture,
        profilePictures,
      }
    : {
        title: t('sellerInfo.reviewDialog'),
        src: sellerLinks.reviews,
        onClose: handleClose,
      };

  return (
    <div>
      {!isProfileLoaded && <TextSkeleton withAnimation={true} />}
      {isProfileLoaded && !Boolean(numberOfReviews) && (
        <>
          <Icon className={Classes.reviewIcon} name="review" color="text-brand-primary" />
          <Trans i18nKey="sellerReviews.noReviews" className={Classes.noReviewsPlaceholder} tagName="span" />
        </>
      )}
      {Boolean(numberOfReviews) && (
        <>
          <Icon className={Classes.reviewIcon} name="saved" color="text-brand-primary" />
          <TextLarge strong>{isRatingValid && `${averageScore.toFixed(1)} · `}</TextLarge>
          <Link href={sellerLinks.reviews} onClick={handleClick}>
            <Trans
              tagName="span"
              i18nKey="sellerInfo.review"
              count={numberOfReviews}
              formattedCount={getFormattedNumber(numberOfReviews)}
            />
          </Link>
        </>
      )}
      {isSellerReviewDialogLoaded && isOpen && <SellerReviewDialog {...props} />}
    </div>
  );
};

export default SellerReviews;
